// ============================================================================
//  CoachPanel.jsx — the SMART goal coach conversation.
//
//  Sends the draft goal to the goal-coach Edge Function and renders the reply.
//  The transcript is deliberately not persisted: only what the man types into
//  the goal fields and saves is kept.
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from "react";
import { X, Sparkles, Send } from "lucide-react";
import { C, display, iconBtn } from "../theme/tokens.js";
import { askCoach } from "../lib/storage.js";
import coachVideos from "../lib/coach-videos.json";

function renderCoachText(text) {
  if (!text) return null;
  const blocks = text.split(/\n\n+/);
  return blocks.map((block, bi) => {
    const lines = block.split("\n");
    const isBulletBlock = lines.every((l) => /^\s*[-*]\s+/.test(l));
    if (isBulletBlock) {
      return (
        <ul key={bi} style={{ margin: "0 0 10px 0", paddingLeft: 20 }}>
          {lines.map((l, li) => (
            <li key={li} style={{ marginBottom: 4, lineHeight: 1.5 }}>
              {renderInline(l.replace(/^\s*[-*]\s+/, ""))}
            </li>
          ))}
        </ul>
      );
    }
    return (
      <p key={bi} style={{ margin: "0 0 10px 0", lineHeight: 1.55 }}>
        {lines.map((l, li) => (
          <React.Fragment key={li}>
            {li > 0 && <br />}
            {renderInline(l)}
          </React.Fragment>
        ))}
      </p>
    );
  });
}

function renderInline(text) {
  const parts = [];
  // First split on markdown links [text](url). Everything else runs through bold pass.
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m;
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: "text", value: text.slice(last, m.index) });
    parts.push({ type: "link", label: m[1], href: m[2] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: "text", value: text.slice(last) });

  return parts.map((part, i) => {
    if (part.type === "link") {
      return (
        <a key={i} href={part.href} target="_blank" rel="noopener noreferrer"
          style={{ color: C.red, textDecoration: "underline" }}>{part.label}</a>
      );
    }
    // Bold + italic pass on plain text parts. The regex tries `**...**` first
    // (bold) and falls through to `*...*` (italic); JS alternation is left-to-
    // right, so bold consumes its markers before italic can grab them.
    const inlineRe = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
    const chunks = [];
    let lastB = 0;
    let bm;
    let n = 0;
    while ((bm = inlineRe.exec(part.value)) !== null) {
      if (bm.index > lastB) chunks.push(part.value.slice(lastB, bm.index));
      const tok = bm[0];
      if (tok.startsWith("**")) {
        chunks.push(<strong key={`b${i}-${n}`}>{tok.slice(2, -2)}</strong>);
      } else {
        chunks.push(<em key={`i${i}-${n}`}>{tok.slice(1, -1)}</em>);
      }
      lastB = bm.index + tok.length;
      n++;
    }
    if (lastB < part.value.length) chunks.push(part.value.slice(lastB));
    return <React.Fragment key={i}>{chunks}</React.Fragment>;
  });
}

export default function CoachPanel({ goal, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  const startedRef = useRef(false);

  const send = useCallback(async (text) => {
    if (!text || busy) return;
    setError("");
    setBusy(true);
    const outgoing = [...messages, { role: "user", content: text }];
    setMessages(outgoing);
    setInput("");
    try {
      const { reply } = await askCoach({
        goal,
        messages: outgoing,
        videos: coachVideos.videos,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (e) {
      setError(e?.message || "Coach unreachable. Save your goal and try again in a moment.");
    } finally {
      setBusy(false);
    }
  }, [busy, goal, messages]);

  // Auto-send the opening prompt exactly once when the panel opens.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    send("Here's what I'm working on. Take a look and tell me where you see me.");
  }, [send]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  function submit(e) {
    e.preventDefault();
    send(input.trim());
  }

  return (
    <div style={{
      marginTop: 18, border: `1px solid ${C.line}`, borderRadius: 12,
      background: "#FBFBFB", overflow: "hidden",
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 14px", borderBottom: `1px solid ${C.line}`, background: "#fff",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Sparkles size={15} color={C.red} />
          <span style={{ fontWeight: 700, fontSize: 13, color: C.text, letterSpacing: ".02em" }}>
            Coach
          </span>
          <span style={{ fontSize: 11, color: C.faint }}>· Haiku</span>
        </div>
        <button type="button" onClick={onClose} style={iconBtn} title="Hide coach">
          <X size={15} />
        </button>
      </div>

      <div ref={scrollRef} style={{
        maxHeight: 260, overflowY: "auto", padding: 14, fontSize: 13.5, color: C.text,
      }}>
        {messages.filter((m, i) => !(i === 0 && m.role === "user")).map((m, i) => (
          <div key={i} style={{
            display: "flex",
            justifyContent: m.role === "user" ? "flex-end" : "flex-start",
            marginBottom: 10,
          }}>
            <div style={{
              maxWidth: "88%",
              padding: "9px 12px",
              borderRadius: 10,
              background: m.role === "user" ? C.redSoft : "#fff",
              border: `1px solid ${m.role === "user" ? "rgba(213,0,50,0.15)" : C.line}`,
              color: C.text,
            }}>
              {renderCoachText(m.content)}
            </div>
          </div>
        ))}
        {busy && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div style={{
              padding: "8px 12px", borderRadius: 10, background: "#fff",
              border: `1px solid ${C.line}`, color: C.sub, fontSize: 12.5, fontStyle: "italic",
            }}>
              Coach is thinking…
            </div>
          </div>
        )}
        {error && (
          <div style={{ marginTop: 8, fontSize: 12, color: C.redDk }}>{error}</div>
        )}
      </div>

      <form onSubmit={submit} style={{
        display: "flex", gap: 8, padding: 10, borderTop: `1px solid ${C.line}`, background: "#fff",
      }}>
        <input value={input} onChange={(e) => setInput(e.target.value)}
          placeholder="Ask the coach…"
          disabled={busy}
          style={{
            flex: 1, padding: "9px 12px", borderRadius: 8, fontSize: 13,
            border: `1px solid ${C.line}`, background: "#FBFBFB",
            color: C.text, outline: "none", boxSizing: "border-box",
          }} />
        <button type="submit" disabled={busy || !input.trim()} style={{
          padding: "0 14px", borderRadius: 8, border: "none",
          background: busy || !input.trim() ? C.line : C.red,
          color: "#fff", cursor: busy || !input.trim() ? "default" : "pointer",
          display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600,
        }}>
          <Send size={13} />
        </button>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  GOAL MODAL                                                         */
/* ------------------------------------------------------------------ */
